import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { InputSwitch } from 'primereact/inputswitch';
import { Dialog } from 'primereact/dialog';
import { RuleListItemOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import RingilDataTable, { ColumnConfig, PagedData } from '@components/RingilDataTable/RingilDataTable';
import { clickableTextClass } from '@components/RingilComponents/clickableText';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';
import SvgPencil from '@icons/pencil-inverted.svg?react';
import SvgCoCopy from '@icons/CoCopy.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';
import EllipsisText from '@components/RingilComponents/EllipsisText';
import { useRuleApi } from './useRuleApi';
import { useRuleMetadata } from './metadata/RuleMetadataProvider';

interface ComponentProps {
  companyId?: string;
  readOnly?: boolean;
  onOpenRule: (ruleId?: string) => void;
}

/**
 * Every rule the company has, highest priority first.
 *
 * The list is not paged by the backend — `GET /rules` returns the whole set — so it is handed to
 * RingilDataTable as a single page with the paginator hidden, rather than faking pagination over
 * data that is already all here.
 */
const RuleList: React.FC<ComponentProps> = ({ companyId, readOnly, onOpenRule }) => {
  const { tr } = useTranslationLgs();
  const { listRules, setRuleEnabled, deleteRule, getRule, saveRule } = useRuleApi(companyId);
  const { triggerByKey } = useRuleMetadata();
  const { loggedUser } = useContext(AuthenticatedUserContext);

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: RuleListItemOutDTO[]; clear: () => void }>(null);
  const [rules, setRules] = useState<RuleListItemOutDTO[]>();
  const [pendingDelete, setPendingDelete] = useState<RuleListItemOutDTO>();
  const [busyRuleId, setBusyRuleId] = useState<string>();

  const load = useCallback(() => {
    listRules(data => setRules([...data].sort((left, right) => right.priority - left.priority || left.name.localeCompare(right.name))));
  }, [listRules]);

  useEffect(() => {
    load();
  }, [load]);

  // The table keeps its own copy of what `loadDataFromApi` handed it, so a reloaded `rules` does not
  // reach the rows on its own — after a toggle the switch would snap back to what it was.
  useEffect(() => {
    tableRef.current?.reloadData();
  }, [rules]);

  const toggleEnabled = (rule: RuleListItemOutDTO) => {
    setBusyRuleId(rule.id);
    setRuleEnabled(rule.id, !rule.enabled, () => {
      setBusyRuleId(undefined);
      load();
    });
  };

  /** Duplicating reads the rule's source and saves it back as a new, disabled one. */
  const duplicate = (rule: RuleListItemOutDTO) => {
    setBusyRuleId(rule.id);
    getRule(rule.id, detail => {
      saveRule(
        undefined,
        {
          sourceText: detail.sourceText,
          name: tr('AiDispatcher.copyOf', '{{name}} (copy)', { name: detail.name }),
          description: detail.description,
          priority: detail.priority,
          // A copy starts switched off, so an accidental duplicate cannot act on its own.
          enabled: false,
        },
        () => {
          setBusyRuleId(undefined);
          load();
        },
      );
    });
  };

  const confirmDelete = () => {
    const rule = pendingDelete;
    setPendingDelete(undefined);
    deleteRule(rule.id, load);
  };

  // Built fresh each render so the row renderers never close over stale state.
  //
  // Widths are percentages and total 100. PrimeReact lays its table out with `table-layout: fixed`,
  // so a width here is literal, not a hint: an absolute one (`8rem`) is a column that will not give
  // any of it back when the window narrows, and widths that overrun the table push the remaining
  // columns' content across their neighbours. Percentages keep every column in proportion at any
  // width, and let the name cell hold its text to one truncated line.
  const columnConfig: ColumnConfig<RuleListItemOutDTO>[] = [
    {
      id: 'enabled',
      header: 'AiDispatcher.enabled',
      headerDefault: 'Enabled',
      width: '7%',
      verticalAlign: 'middle',
      render: row => <InputSwitch checked={row.enabled} disabled={readOnly || busyRuleId === row.id} onChange={() => toggleEnabled(row)} />,
    },
    {
      id: 'name',
      header: 'AiDispatcher.ruleName',
      headerDefault: 'Name',
      width: '29%',
      verticalAlign: 'middle',
      render: row => (
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-2">
            {/* A plain span rather than RingilButton `link`: the button wraps its label in a flex item
                that cannot shrink, so the text would never reach its ellipsis. clickableTextClass is
                the same colour/hover rule the link button composes. */}
            <EllipsisText className={`${clickableTextClass} min-w-0`} onClick={() => onOpenRule(row.id)}>
              {row.name}
            </EllipsisText>
            {/* A paused rule is otherwise indistinguishable from one nothing has happened to, and
                the two call for opposite reactions. It is not disabled — the pause heals itself. */}
            {row.pausedUntil && (
              <span
                className="shrink-0 whitespace-nowrap rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                title={tr('AiDispatcher.pausedTooltip', 'Paused after five failed runs in a row. It will try again by itself; the failures are in the execution log.')}
              >
                {tr('AiDispatcher.pausedUntil', 'Paused until {{until}}', {
                  until: stringDateTimeWithLocaleFromApiStringInTz(row.pausedUntil, loggedUser?.defaultTimezoneId),
                })}
              </span>
            )}
          </div>
          {row.description && <EllipsisText className="text-sm text-content-secondary">{row.description}</EllipsisText>}
        </div>
      ),
    },
    {
      id: 'trigger',
      header: 'AiDispatcher.trigger',
      headerDefault: 'Trigger',
      width: '15%',
      verticalAlign: 'middle',
      render: row => {
        const trigger = triggerByKey(row.triggerType);
        return <span>{trigger ? tr(trigger.i18nKey, trigger.key) : row.triggerType}</span>;
      },
    },
    {
      id: 'priority',
      header: 'AiDispatcher.priority',
      headerDefault: 'Priority',
      width: '7%',
      verticalAlign: 'middle',
      render: row => <span>{row.priority}</span>,
    },
    {
      id: 'lastFired',
      header: 'AiDispatcher.lastFired',
      headerDefault: 'Last fired',
      width: '16%',
      verticalAlign: 'middle',
      render: row => <span>{row.lastFiredAt ? stringDateTimeWithLocaleFromApiStringInTz(row.lastFiredAt, loggedUser?.defaultTimezoneId) : '—'}</span>,
    },
    {
      id: 'executions',
      header: 'AiDispatcher.executions24h',
      headerDefault: 'Runs (24 h)',
      width: '10%',
      verticalAlign: 'middle',
      render: row => <span>{row.executionCount24h}</span>,
    },
    {
      id: 'actions',
      header: 'AiDispatcher.actionsColumn',
      headerDefault: '',
      // Icon-only buttons, as in the other row-action tables: three labelled buttons needed 22rem and
      // their translations are what pushed the name column into a squeeze. Each one carries a tooltip,
      // since a bare glyph says nothing on its own. 16% still holds three 32px buttons at the narrowest
      // width this table renders at — below 1024px RingilDataTable switches to mobile cards.
      width: '16%',
      verticalAlign: 'middle',
      render: row => (
        <div className="flex flex-nowrap items-center justify-end gap-1 whitespace-nowrap [&>button]:shrink-0">
          <RingilButton mode="table" icon={<SvgPencil />} tooltip={tr('AiDispatcher.edit', 'Edit')} onClick={() => onOpenRule(row.id)} />
          {!readOnly && (
            <>
              <RingilButton
                mode="table"
                icon={<SvgCoCopy />}
                tooltip={tr('AiDispatcher.duplicateTooltip', 'Create a disabled copy of this rule')}
                onClick={() => duplicate(row)}
                disabled={busyRuleId === row.id}
              />
              <RingilButton mode="table" icon={<SvgDelete />} tooltip={tr('AiDispatcher.delete', 'Delete')} onClick={() => setPendingDelete(row)} />
            </>
          )}
        </div>
      ),
    },
  ];

  const loadDataFromApi = (_filter: unknown, _pageable: unknown, onSuccess: (paged: PagedData<RuleListItemOutDTO>) => void) => {
    const data = rules ?? [];
    onSuccess({ data, total: data.length, page: 1, size: data.length || 1 });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        {!readOnly && <RingilButton mode="filled-rectangle" label={tr('AiDispatcher.newRule', 'New rule')} onClick={() => onOpenRule(undefined)} />}
      </div>

      {rules && rules.length === 0 ? (
        <div className="rounded-md border border-dashed border-base-tertiary p-6 text-center text-content-secondary">
          {tr('AiDispatcher.noRules', 'No rules yet. Create one to let the robot act on your expeditions.')}
        </div>
      ) : (
        // PrimeReact gives its table `table-layout: fixed` but only `min-width: 100%`, so the table
        // width is whatever its content asks for — and percentage column widths have nothing definite
        // to resolve against, which is why one long rule name used to set the name column's width and
        // keep it while the window narrowed. Pinning the table to the width it is given makes the
        // percentages real: measured 1117px → 450/357/283px for the name column at 1600/1280/1024.
        <div className="[&_.p-datatable_table]:w-full">
          <RingilDataTable
            ref={tableRef}
            columnConfig={columnConfig}
            loadDataFromApi={loadDataFromApi}
            filterName="aiDispatcherRules"
            hidePaginator
            enableFilterV2={false}
          />
        </div>
      )}

      <Dialog
        visible={pendingDelete !== undefined}
        onHide={() => setPendingDelete(undefined)}
        header={tr('AiDispatcher.deleteRuleTitle', 'Delete this rule?')}
        footer={
          <div className="flex justify-end gap-2">
            <RingilButton mode="text-only" label={tr('AiDispatcher.cancel', 'Cancel')} onClick={() => setPendingDelete(undefined)} />
            <RingilButton mode="filled-rectangle" label={tr('AiDispatcher.delete', 'Delete')} onClick={confirmDelete} />
          </div>
        }
      >
        <p className="m-0 max-w-lg">{tr('AiDispatcher.deleteRuleWarning', 'The rule is removed for good. Executions it already produced are kept.')}</p>
      </Dialog>
    </div>
  );
};

export default RuleList;
